
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { getGhlAiSettings, updateGhlAiSettings, getGhlAiUsage } from "@/api/ghlAi.api.js";
import { fetchGhlPaymentCatalogList } from "@/api/ghlPayment.api.js";
import AiDmOnboarding from "@/components/ai/AiDmOnboarding.vue";
import { getActiveInstance } from "@/utils/ghlInstance.js";

const GHL_APP_BASE_URL = (process.env.VUE_APP_GHL_APP_BASE_URL || "https://app.ai247saas.com").replace(/\/$/, "");
const AI_CREDIT_TOP_UP_SKU = "854";

export default defineComponent({
  name: "ai-settings",
  components: {
    AiDmOnboarding
  },
  props: {
    hasGhlLocation: {
      type: Boolean,
      default: false
    },
    locationId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      completingOnboarding: false,
      resettingOnboarding: false,
      aiEnabled: false,
      globalAiEnabled: false,
      dmOnboardingCompleted: false,
      canEnableAi: false,
      ghlIntegrationsUrl: "",
      defaultAgentId: null as number | null,
      availableAgents: [] as any[],
      loadError: "",
      usageLoaded: false,
      usageError: "",
      usageSummary: {
        chatReplyCount: 0
      },
      usageByEventType: [] as any[],
      usagePeriodStart: "",
      usagePeriodEnd: "",
      usageCredits: {
        displayCreditsRemaining: 0,
        displayCreditsUsedInPeriod: 0,
        creditsDepleted: false
      },
      topUpLoading: false
    };
  },
  computed: {
    usagePeriodLabel(): string {
      if (!this.usagePeriodStart || !this.usagePeriodEnd) {
        return "";
      }
      const start = new Date(this.usagePeriodStart);
      const end = new Date(this.usagePeriodEnd);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return "";
      }
      const formatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
      return `${formatter.format(start)} – ${formatter.format(end)}`;
    },
    resolvedIntegrationsUrl(): string {
      if (this.ghlIntegrationsUrl) {
        return this.ghlIntegrationsUrl;
      }
      if (!this.locationId) {
        return "";
      }
      return `${GHL_APP_BASE_URL}/v2/location/${this.locationId}/settings/lc-integrations`;
    }
  },
  async mounted() {
    await Promise.all([this.loadSettings(), this.loadUsage()]);
  },
  methods: {
    formatNumber(value: number) {
      return new Intl.NumberFormat().format(Number(value) || 0);
    },
    formatEventType(eventType: string) {
      const labels: Record<string, string> = {
        chat_reply: "Chat replies",
        embed_query: "RAG queries",
        embed_index: "KB indexing",
        conversation_summary: "Conversation summaries",
        follow_up: "Follow-up messages",
        image_read: "Image reading",
        social_caption_draft: "Post caption drafts",
        social_image_generate: "Post image generation",
        social_image_moderate: "Post image moderation",
        social_image_prompt: "Post image prompt helper",
        fanout_caption_rewrite: "Caption rewrites",
        fanout_content_moderation: "Content moderation"
      };
      return labels[eventType] || eventType;
    },
    applyUsageData(data: Record<string, unknown>) {
      const summary = (data.summary || {}) as Record<string, unknown>;
      this.usageSummary = {
        chatReplyCount: Number(summary.chatReplyCount) || 0
      };
      this.usageByEventType = Array.isArray(data.byEventType) ? data.byEventType : [];
      this.usagePeriodStart = String(data.periodStart || "");
      this.usagePeriodEnd = String(data.periodEnd || "");
      const credits = (data.credits || {}) as Record<string, unknown>;
      this.usageCredits = {
        displayCreditsRemaining: Number(credits.displayCreditsRemaining) || 0,
        displayCreditsUsedInPeriod: Number(credits.displayCreditsUsedInPeriod) || 0,
        creditsDepleted: Boolean(credits.creditsDepleted)
      };
      this.usageLoaded = true;
    },
    async loadUsage() {
      this.usageError = "";
      try {
        const response = await getGhlAiUsage();
        this.applyUsageData(response.data || {});
      } catch (err) {
        this.usageLoaded = false;
        this.usageError = (err as any)?.message || "Failed to load AI usage.";
      }
    },
    applySettingsData(data: Record<string, unknown>) {
      this.aiEnabled = Boolean(data.aiEnabled);
      this.globalAiEnabled = Boolean(data.globalAiEnabled);
      this.dmOnboardingCompleted = Boolean(data.dmOnboardingCompleted);
      this.canEnableAi = Boolean(data.canEnableAi);
      this.ghlIntegrationsUrl = String(data.ghlIntegrationsUrl || "");
      this.defaultAgentId = data.defaultAgentId
        ? Number(data.defaultAgentId)
        : null;
      this.availableAgents = Array.isArray(data.availableAgents)
        ? data.availableAgents
        : [];
    },
    async loadSettings() {
      this.loading = true;
      this.loadError = "";
      try {
        const response = await getGhlAiSettings();
        this.applySettingsData(response.data || {});
      } catch (err) {
        this.loadError = (err as any)?.message || "Failed to load AI settings.";
      } finally {
        this.loading = false;
      }
    },
    async completeOnboarding() {
      this.completingOnboarding = true;
      try {
        const response = await updateGhlAiSettings({
          dmOnboardingCompleted: true,
          aiEnabled: false
        });
        this.applySettingsData(response.data || {});
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Messenger setup marked complete",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not save onboarding status",
          text: (err as any)?.message || "Please try again."
        });
      } finally {
        this.completingOnboarding = false;
      }
    },
    async resetOnboarding() {
      this.resettingOnboarding = true;
      try {
        const response = await updateGhlAiSettings({
          dmOnboardingCompleted: false,
          aiEnabled: false
        });
        this.applySettingsData(response.data || {});
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not update onboarding status",
          text: (err as any)?.message || "Please try again."
        });
      } finally {
        this.resettingOnboarding = false;
      }
    },
    async startCreditTopUp() {
      if (this.topUpLoading) return;

      this.topUpLoading = true;
      try {
        const { data } = await fetchGhlPaymentCatalogList();
        if (!data?.success) {
          throw new Error(data?.message || "Product catalog is unavailable.");
        }

        const locationId = String(data.locationId || "").trim();
        if (!locationId) {
          throw new Error("Product catalog location is not configured.");
        }

        const products = Array.isArray(data.products) ? data.products : [];
        const hasTopUpProduct = products.some(
          (product: { sku?: string | number }) => String(product?.sku || "") === AI_CREDIT_TOP_UP_SKU
        );
        if (!hasTopUpProduct) {
          throw new Error("AI credit top-up product is not available right now.");
        }

        const query: Record<string, string> = {
          locationId,
          sku: AI_CREDIT_TOP_UP_SKU
        };
        const ecpid = getActiveInstance();
        if (ecpid) {
          query.ecpid = ecpid;
        }

        await this.$router.push({ path: "/pay", query });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not start checkout",
          text: (err as any)?.response?.data?.message || (err as any)?.message || "Please try again."
        });
      } finally {
        this.topUpLoading = false;
      }
    },
    async saveSettings() {
      if (!this.hasGhlLocation || !this.dmOnboardingCompleted) {
        this.aiEnabled = false;
        return;
      }

      if (this.aiEnabled && !this.defaultAgentId) {
        this.aiEnabled = false;
        Swal.fire({
          icon: "warning",
          title: "Select an AI agent",
          text: "Choose an AI agent before enabling AI auto-reply."
        });
        return;
      }

      this.saving = true;
      try {
        const response = await updateGhlAiSettings({
          aiEnabled: this.aiEnabled,
          dmOnboardingCompleted: this.dmOnboardingCompleted,
          defaultAgentId: this.defaultAgentId
        });
        this.applySettingsData(response.data || {});
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: this.aiEnabled ? "AI auto-reply enabled" : "AI settings saved",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not update AI settings",
          text: (err as any)?.response?.data?.message || (err as any)?.message || "Please try again."
        });
        await this.loadSettings();
      } finally {
        this.saving = false;
      }
    }
  }
});
